/**
 * ProPilote - Offline Sync Engine
 *
 * Replays queued mutations when the network comes back.
 * - Listens to 'online' event + retries on a 30s tick when navigator.onLine
 * - Retry with exponential backoff: max 5 attempts (1s, 5s, 30s, 2min, 5min)
 * - On final failure, marks the mutation as 'failed' so the user can retry manually
 *
 * Strategy "chauffeur wins" : the queued request is replayed AS-IS
 * — the server accepts the latest update from the field driver.
 */

import axios from 'axios';
import {
  listMutations, updateMutation, deleteMutation, countPending,
  listBlobs, updateBlob, deleteBlob, countPendingBlobs,
} from './offlineDB';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_ATTEMPTS = 5;
const BACKOFF_MS = [1000, 5000, 30_000, 120_000, 300_000];

const listeners = new Set();
let isSyncing = false;
let intervalId = null;

const emit = () => {
  // Fire to React subscribers
  Promise.all([countPending(), countPendingBlobs()]).then(([m, b]) => {
    const pending = m + b;
    listeners.forEach((cb) => {
      try { cb({ pending, mutations: m, blobs: b, isSyncing, online: navigator.onLine }); } catch {}
    });
  });
};

export const subscribe = (cb) => {
  listeners.add(cb);
  emit();
  return () => listeners.delete(cb);
};

const replayOne = async (mut) => {
  const config = {
    method: mut.method,
    url: mut.url.startsWith('http') ? mut.url : `${API_URL}${mut.url}`,
    headers: mut.headers || {},
  };
  // Re-attach a fresh token (token may have rotated since enqueue)
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (mut.data !== null && mut.data !== undefined) {
    config.data = mut.data;
  }
  try {
    const res = await axios(config);
    return { ok: true, status: res.status };
  } catch (err) {
    const status = err.response?.status;
    const msg = err.response?.data?.detail || err.message || 'Erreur reseau';
    return { ok: false, status, error: msg };
  }
};

/**
 * Replay a single blob upload (FormData with the actual file).
 */
const replayBlob = async (b) => {
  const token = localStorage.getItem('token');
  const url = b.upload_url.startsWith('http') ? b.upload_url : `${API_URL}${b.upload_url}`;

  // Resolve local-{...} report ids if the report has been created in the meantime
  let resolvedUrl = url;
  if (resolvedUrl.includes('/local-')) {
    // Cannot replay yet: skip until the parent report sync replaces local id
    return { ok: false, status: 409, error: 'En attente du rapport parent' };
  }

  const fd = new FormData();
  fd.append('file', b.blob, b.filename);
  Object.entries(b.extra_form || {}).forEach(([k, v]) => fd.append(k, v));

  try {
    const r = await fetch(resolvedUrl, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    if (r.ok) return { ok: true, status: r.status };
    return { ok: false, status: r.status, error: `HTTP ${r.status}` };
  } catch (err) {
    return { ok: false, status: 0, error: err.message || 'Erreur reseau' };
  }
};

/**
 * Run one sync cycle: process all pending mutations.
 * Idempotent: safe to call multiple times.
 */
export const runSync = async () => {
  if (isSyncing) return;
  if (!navigator.onLine) return;
  isSyncing = true;
  emit();
  try {
    // 1. Mutations (JSON requests)
    const pending = await listMutations('pending');
    for (const mut of pending) {
      const attempt = mut.attempts || 0;
      const wait = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
      if (mut.updated_at && Date.now() - mut.updated_at < wait && attempt > 0) continue;
      await updateMutation(mut.id, { status: 'syncing', attempts: attempt + 1 });
      const r = await replayOne(mut);
      if (r.ok) {
        await deleteMutation(mut.id);
      } else if (r.status >= 400 && r.status < 500 && r.status !== 408 && r.status !== 429) {
        await updateMutation(mut.id, { status: 'failed', last_error: `HTTP ${r.status}: ${r.error}` });
      } else {
        await updateMutation(mut.id, {
          status: attempt + 1 >= MAX_ATTEMPTS ? 'failed' : 'pending',
          last_error: r.error,
        });
      }
    }

    // 2. Blobs (FormData/file uploads)
    const blobs = await listBlobs('pending');
    for (const b of blobs) {
      const attempt = b.attempts || 0;
      const wait = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
      if (b.updated_at && Date.now() - b.updated_at < wait && attempt > 0) continue;
      await updateBlob(b.id, { status: 'syncing', attempts: attempt + 1 });
      const r = await replayBlob(b);
      if (r.ok) {
        await deleteBlob(b.id);
      } else if (r.status >= 400 && r.status < 500 && r.status !== 408 && r.status !== 429 && r.status !== 409) {
        await updateBlob(b.id, { status: 'failed', last_error: `HTTP ${r.status}: ${r.error}` });
      } else {
        await updateBlob(b.id, {
          status: attempt + 1 >= MAX_ATTEMPTS ? 'failed' : 'pending',
          last_error: r.error,
        });
      }
    }
  } finally {
    isSyncing = false;
    emit();
  }
};

/**
 * Manually retry a failed mutation (user clicks "retry").
 */
export const retryMutation = async (id) => {
  await updateMutation(id, { status: 'pending', attempts: 0, last_error: null });
  return runSync();
};

/**
 * Boot: wire up online/offline listeners and a retry tick.
 * Call once at app startup.
 */
export const startSyncEngine = () => {
  if (intervalId) return; // idempotent
  window.addEventListener('online', () => {
    emit();
    runSync();
  });
  window.addEventListener('offline', emit);
  // Periodic retry every 30s while online
  intervalId = setInterval(() => {
    if (navigator.onLine) runSync();
  }, 30_000);
  // Listen to SW Background Sync wake-up messages
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (e) => {
      if (e.data && e.data.type === 'OFFLINE_SYNC_RUN') runSync();
    });
  }
  // Fire once immediately
  if (navigator.onLine) runSync();
  emit();
};

export const stopSyncEngine = () => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

export default { startSyncEngine, runSync, retryMutation, subscribe };
